import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Lock,
  MessageSquare,
} from 'lucide-react';

import { Avatar, Badge, Button } from '../../../components/ui';
import { trpc } from '../../../lib/trpc';

export const Route = createFileRoute('/app/users/$userId')({
  staticData: { title: 'User Profile' },
  component: PublicProfilePage,
});

function formatRelativeDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

const statusMap = {
  online: { label: 'Online', color: 'green' as const, dotClass: 'bg-green-500' },
  offline: { label: 'Offline', color: 'gray' as const, dotClass: 'bg-surface-500' },
  in_game: { label: 'In Game', color: 'blue' as const, dotClass: 'bg-blue-500' },
  in_queue: { label: 'In Queue', color: 'yellow' as const, dotClass: 'bg-yellow-500' },
};

function PublicProfilePage() {
  const { userId } = Route.useParams();
  const { data: user, isLoading, error } = trpc.user.getProfile.useQuery({ id: userId });
  const { data: currentUser } = trpc.user.me.useQuery();

  if (isLoading) return <ProfileSkeleton />;

  if (error || !user) {
    return (
      <div className="max-w-4xl mx-auto p-4 md:p-6 flex flex-col items-center justify-center py-24">
        <h2 className="text-xl font-display font-bold mb-2">User Not Found</h2>
        <p className="text-surface-400 text-sm mb-6">This user doesn't exist or has been removed.</p>
        <Link to="/app" className="text-brand-400 hover:underline text-sm">
          Back to Hub
        </Link>
      </div>
    );
  }

  const isOwnProfile = currentUser?.id === user.id;
  const status = statusMap[user.status] ?? statusMap.offline;

  // Private profile — limited view
  if (!user.isPublic && !isOwnProfile) {
    return (
      <div className="max-w-4xl mx-auto p-4 md:p-6">
        <Link to="/app" className="inline-flex items-center gap-1.5 text-sm text-surface-400 hover:text-surface-200 mb-6 transition-colors">
          <ArrowLeft className="size-4" />
          Back
        </Link>

        <div className="relative rounded-2xl overflow-hidden animate-fade-up">
          <div className="absolute inset-0 bg-gradient-to-br from-surface-800 via-surface-850 to-surface-900 noise-overlay" />
          <div className="relative px-8 py-12 flex flex-col items-center text-center">
            <Avatar
              initials={user.username.slice(0, 2)}
              size="xl"
              bordered={false}
            />
            <h1 className="text-2xl font-display font-bold mt-4 mb-1">{user.username}</h1>
            <Badge color={status.color} variant="solid" size="sm">
              {status.label}
            </Badge>
            <div className="flex items-center gap-2 mt-6 text-surface-400">
              <Lock className="size-4" />
              <span className="text-sm">This profile is private</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If viewing own profile, redirect hint
  if (isOwnProfile) {
    return (
      <div className="max-w-4xl mx-auto p-4 md:p-6">
        <div className="flex items-center justify-between mb-6">
          <Link to="/app" className="inline-flex items-center gap-1.5 text-sm text-surface-400 hover:text-surface-200 transition-colors">
            <ArrowLeft className="size-4" />
            Back
          </Link>
          <Link to="/app/profile">
            <Button variant="outline" size="sm">
              Edit Profile
            </Button>
          </Link>
        </div>
        {renderProfile(user, status)}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <Link to="/app" className="inline-flex items-center gap-1.5 text-sm text-surface-400 hover:text-surface-200 transition-colors">
          <ArrowLeft className="size-4" />
          Back
        </Link>
        <Button
          variant="outline"
          size="sm"
          leftIcon={<MessageSquare className="size-3.5" />}
        >
          Message
        </Button>
      </div>
      {renderProfile(user, status)}
    </div>
  );
}

function renderProfile(
  user: { id: string; username: string; bio?: string | null; status: string; lastOnlineAt?: Date | string | null; createdAt?: Date | string | null; isPublic: boolean },
  status: { label: string; color: 'green' | 'gray' | 'blue' | 'yellow'; dotClass: string },
) {
  return (
    <>
      {/* Hero Banner */}
      <div className="relative rounded-2xl overflow-hidden mb-8 animate-fade-up">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-900/60 via-surface-800 to-surface-900 noise-overlay" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-500/8 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-500/5 rounded-full blur-3xl" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-500/40 to-transparent" />

        <div className="relative px-8 pt-10 pb-8">
          <div className="flex items-end gap-6">
            {/* Avatar with glow ring */}
            <div className="relative shrink-0">
              <div className="absolute -inset-1.5 rounded-full bg-gradient-to-br from-brand-500/40 to-brand-700/20 blur-sm" />
              <div className="absolute -inset-0.5 rounded-full bg-gradient-to-br from-brand-500/60 to-brand-700/30" />
              <div className="relative">
                <Avatar
                  initials={user.username.slice(0, 2)}
                  size="xl"
                  bordered={false}
                />
              </div>
              <div className={`absolute -bottom-0.5 -right-0.5 size-4 ${status.dotClass} rounded-full border-[3px] border-surface-800`} />
            </div>

            {/* Name + meta */}
            <div className="flex-1 min-w-0 pb-1">
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-3xl font-display font-bold tracking-tight truncate">
                  {user.username}
                </h1>
                <Badge color={status.color} variant="solid" size="sm">
                  {status.label}
                </Badge>
              </div>

              {user.bio ? (
                <p className="text-surface-200 text-sm leading-relaxed max-w-lg mb-3">
                  {user.bio}
                </p>
              ) : (
                <p className="text-surface-400 text-sm italic mb-3">
                  No bio set
                </p>
              )}

              <div className="flex items-center gap-3 text-xs">
                {user.createdAt && (
                  <span className="flex items-center gap-1.5 text-surface-300 bg-surface-800/80 px-2.5 py-1 rounded-full">
                    <Calendar className="size-3" />
                    Joined {new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </span>
                )}
                {user.lastOnlineAt && user.status === 'offline' && (
                  <span className="flex items-center gap-1.5 text-surface-300 bg-surface-800/80 px-2.5 py-1 rounded-full">
                    <Clock className="size-3" />
                    Last seen {formatRelativeDate(user.lastOnlineAt)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function ProfileSkeleton() {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="h-5 w-16 bg-surface-700/30 rounded animate-pulse mb-6" />
      <div className="relative rounded-2xl overflow-hidden mb-8 bg-surface-800/50 border border-surface-700/50">
        <div className="px-8 pt-10 pb-8">
          <div className="flex items-end gap-6">
            <div className="size-16 rounded-full bg-surface-700 animate-pulse shrink-0" />
            <div className="flex-1 space-y-3 pb-1">
              <div className="h-8 w-56 bg-surface-700 rounded animate-pulse" />
              <div className="h-4 w-80 bg-surface-700/50 rounded animate-pulse" />
              <div className="flex gap-2">
                <div className="h-6 w-28 bg-surface-700/30 rounded-full animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
